/**
 * Picks entities from the case graph by name. An entity is kept by its stable `_uid` (ADR-0091);
 * one without a uid (written before the stable ids) cannot be picked. The search itself is the
 * feature's: this shows its answer. The copy is the caller's, so each form names what it picks.
 */
import { Plus, X } from 'lucide-react'
import { useId } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** An entity the search found, by its stable id. */
export interface EntityOption {
  uid: string
  name: string
  label: string | null
}

/** The feature's entity search, as the picker drives and shows it. */
export interface EntitySearch {
  query: string
  onQueryChange: (query: string) => void
  status: 'idle' | 'searching' | 'ready' | 'failed'
  results: readonly EntityOption[]
}

/** What the picker says, already translated. */
export interface EntityPickerCopy {
  label: string
  placeholder: string
  hint: string
  searching: string
  failed: string
  noResults: string
  remove: (name: string) => string
}

export interface EntityPickerProps {
  search: EntitySearch
  selected: readonly EntityOption[]
  onChange: (selected: EntityOption[]) => void
  copy: EntityPickerCopy
}

function Results({ search, selected, onChange, copy }: EntityPickerProps) {
  if (search.status === 'idle') return <p className="text-2xs text-muted-foreground">{copy.hint}</p>
  if (search.status === 'searching') {
    return <p className="text-2xs text-muted-foreground">{copy.searching}</p>
  }
  if (search.status === 'failed') return <p className="text-2xs text-destructive">{copy.failed}</p>
  const chosen = new Set(selected.map(entity => entity.uid))
  const open = search.results.filter(entity => !chosen.has(entity.uid))
  if (open.length === 0) return <p className="text-2xs text-muted-foreground">{copy.noResults}</p>
  return (
    <ul className="flex max-h-40 flex-col gap-0.5 overflow-y-auto">
      {open.map(entity => (
        <li key={entity.uid}>
          <button
            type="button"
            onClick={() => onChange([...selected, entity])}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent"
          >
            <Plus aria-hidden className="size-3 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{entity.name}</span>
            {entity.label && (
              <span className="shrink-0 text-2xs text-muted-foreground">{entity.label}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  )
}

export function EntityPicker(props: EntityPickerProps) {
  const inputId = useId()
  const { search, selected, onChange, copy } = props
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>{copy.label}</Label>
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {selected.map(entity => (
            <li key={entity.uid}>
              <Badge variant="outline" className="h-6 pr-1">
                {entity.name}
                <button
                  type="button"
                  aria-label={copy.remove(entity.name)}
                  onClick={() => onChange(selected.filter(other => other.uid !== entity.uid))}
                  className="rounded-sm text-muted-foreground hover:text-foreground"
                >
                  <X aria-hidden className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={inputId}
        value={search.query}
        onChange={event => search.onQueryChange(event.target.value)}
        placeholder={copy.placeholder}
        autoComplete="off"
      />
      <Results {...props} />
    </div>
  )
}
